import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import GoToDashboardButton from './GoToDashboardButton';
import styles from './ProductionRootCause.module.css';
import SourceFileSummary from './SourceFileSummary';
import {
  artavaRechartsAxis,
  artavaRechartsGrid,
  artavaRechartsTooltip,
} from './chartPalette';
import { fetchRetryPressureAnalysis } from './productionRootCause/productionRootCauseApi';

const defaultFilters = {
  startDate: '',
  endDate: '',
  ateSwVersion: '',
  stationName: '',
  selectedFamilies: [],
};

const RetryPressure = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState({ summary: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const topRows = useMemo(() => data.rows.slice(0, 12), [data.rows]);

  const loadData = async (nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchRetryPressureAnalysis(nextFilters);
      setData(response || { summary: null, rows: [] });
    } catch (requestError) {
      setError(requestError.message || 'Failed to load retry pressure analysis.');
      setData({ summary: null, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    loadData(defaultFilters);
  };

  const summary = data.summary || {};

  return (
    <div className={styles.pageContainer}>
      <GoToDashboardButton />

      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Root problem idea 5</p>
          <h1 className={styles.title}>Retry Pressure</h1>
          <p className={styles.description}>
            Find the sections that consume retries, rework time, and operator attention. This page groups the section-level retry data and shows where the process is burning time before the final result is even visible.
          </p>
        </div>
      </header>

      <section className={styles.filterPanel}>
        <div className={styles.filterHeader}>
          <div>
            <h3>Filters</h3>
            <p>Use this page when the line feels slow or unstable even before fail counts spike. Retry-heavy sections are often the earliest signal of fragile behavior.</p>
          </div>
        </div>
        <div className={styles.filterGrid}>
          <div className={styles.filterItem}>
            <label>Start date</label>
            <input type="date" value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>End date</label>
            <input type="date" value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>ATE SW version</label>
            <input value={filters.ateSwVersion} onChange={(event) => updateFilter('ateSwVersion', event.target.value)} placeholder="All versions" />
          </div>
          <div className={styles.filterItem}>
            <label>Station name</label>
            <input value={filters.stationName} onChange={(event) => updateFilter('stationName', event.target.value)} placeholder="All stations" />
          </div>
          <div className={styles.filterActions}>
            <button className={styles.primaryButton} onClick={() => loadData()} disabled={loading}>Run analysis</button>
            <button className={styles.secondaryButton} onClick={resetFilters}>Reset</button>
          </div>
        </div>
        <SourceFileSummary
          compact
          startDate={filters.startDate}
          endDate={filters.endDate}
          ateSwVersion={filters.ateSwVersion}
          selectedFamilies={filters.selectedFamilies}
          onFamiliesChange={(values) => updateFilter('selectedFamilies', values)}
          title="Source files used for dashboard charts"
        />
      </section>

      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.metricGrid}>
        <div className={styles.metricCard}><span>Section rows</span><strong>{summary.sectionRows || 0}</strong></div>
        <div className={styles.metricCard}><span>Sections analyzed</span><strong>{summary.sectionsAnalyzed || 0}</strong></div>
        <div className={styles.metricCard}><span>Total retries</span><strong>{summary.totalRetries || 0}</strong></div>
        <div className={styles.metricCard}><span>Top retry score</span><strong>{summary.topRetryScore || 0}</strong></div>
      </section>

      {loading ? (
        <div className={styles.loading}>Analyzing retry burden...</div>
      ) : data.rows.length === 0 ? (
        <div className={styles.emptyState}>No retry patterns matched this slice.</div>
      ) : (
        <>
          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Retry load by section</h3>
                <p>Bars show total retries; the line shows how often that section needs any retry at all.</p>
              </div>
            </div>
            <div className={styles.chartBox}>
              <ResponsiveContainer>
                <ComposedChart data={topRows} margin={{ top: 10, right: 20, bottom: 45, left: 0 }}>
                  <CartesianGrid {...artavaRechartsGrid} vertical={false} />
                  <XAxis dataKey="testStepNumber" angle={-35} textAnchor="end" height={75} {...artavaRechartsAxis} />
                  <YAxis yAxisId="left" {...artavaRechartsAxis} />
                  <YAxis yAxisId="right" orientation="right" {...artavaRechartsAxis} />
                  <Tooltip {...artavaRechartsTooltip} />
                  <Bar yAxisId="left" dataKey="totalRetries" name="Total retries" fill="#8a3f5d" radius={[6, 6, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="retryRate" name="Retry rate %" stroke="#24745f" strokeWidth={3} dot={{ r: 4, fill: '#24745f' }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className={styles.insightGrid}>
            {data.rows.slice(0, 3).map((row) => (
              <article className={styles.insightCard} key={`${row.testIndex}-${row.testStepNumber}-${row.testName}`}>
                <p className={styles.eyebrow}>Retry hotspot</p>
                <h3>{row.testStepNumber} - {row.testName}</h3>
                <p>This section generated {row.totalRetries} retries, with retries in {row.retryRate}% of entries and a fail rate of {row.failRate}%.</p>
                <div className={styles.chips}>
                  <span>{row.avgSectionTime} sec avg</span>
                  <span>{row.affectedSerials} serials</span>
                </div>
              </article>
            ))}
          </section>

          <section className={styles.tablePanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Retry evidence</h3>
                <p>Sections with high retry pressure slow the line even before they become obvious fail hotspots.</p>
              </div>
            </div>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Section</th>
                  <th>Entries</th>
                  <th>Total retries</th>
                  <th>Avg retries</th>
                  <th>Retry rate</th>
                  <th>Fail rate</th>
                  <th>Avg time</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, index) => (
                  <tr key={`${row.testIndex}-${row.testStepNumber}-${row.testName}`}>
                    <td>{index + 1}</td>
                    <td className={styles.stepName}>{row.testStepNumber}<br /><span className={styles.muted}>{row.testName}</span></td>
                    <td>{row.entries}</td>
                    <td>{row.totalRetries}</td>
                    <td>{row.avgRetries}</td>
                    <td>{row.retryRate}%</td>
                    <td>{row.failRate}%</td>
                    <td>{row.avgSectionTime} sec</td>
                    <td>{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
};

export default RetryPressure;
